import agent, { ListData } from "../api/agent";
import { DataTableList } from "../common/interfaces/TableData";
import { FamilyMember } from "../models/familyMember";

export type FamilyMemberStore = {
    tableData: DataTableList<FamilyMember>;
    loadingInitial: boolean;
    loading: boolean;

    loadFamilyMembersForMember: (memberId: string, filterProps?: any) => Promise<ListData<FamilyMember> | void>;
    loadFamilyMember: (id: string) => Promise<FamilyMember | undefined>;
    createFamilyMember: (fm: FamilyMember) => Promise<void>;
    updateFamilyMember: (fm: FamilyMember) => Promise<void>;
    deleteFamilyMember: (id: string) => Promise<void>;
};

export const createFamilyMemberStore = (set: any, get: any): FamilyMemberStore => ({
    tableData: new DataTableList<FamilyMember>(),
    loadingInitial: false,
    loading: false,

    loadFamilyMembersForMember: async (memberId: string, filterProps?: any) => {
        set((s: any) => ({ familyMemberStore: { ...s.familyMemberStore, loadingInitial: true } }));
        try {
            const result: ListData<FamilyMember> = await agent.FamilyMembers.list(memberId, filterProps);
            const list = result.data.reduce((acc: Map<string, FamilyMember>, item: FamilyMember) => {
                acc.set(item.id!, item);
                return acc;
            }, new Map<string, FamilyMember>());
            set((s: any) => ({
                familyMemberStore: {
                    ...s.familyMemberStore,
                    tableData: {
                        list,
                        currentPage: result.currentPage,
                        pageSize: result.pageSize,
                        totalRecords: result.totalRecords,
                        orderBy: result.orderBy,
                    },
                    loadingInitial: false,
                },
            }));
            return result;
        } catch (e) {
            console.error(e);
            set((s: any) => ({ familyMemberStore: { ...s.familyMemberStore, loadingInitial: false } }));
        }
    },

    loadFamilyMember: async (id: string) => {
        const fm = get().familyMemberStore.tableData.list.get(id);
        if (fm) return fm;
        const entity = await agent.FamilyMembers.details(id);
        return entity;
    },

    createFamilyMember: async (fm: FamilyMember) => {
        const { id, firstName, lastName, dateOfBirth, relationShipType, ...rest } = fm;
        const fmFormValues = {
            id,
            firstName,
            lastName,
            dateOfBirth,
            relationShipType: undefined,
            ...rest,
            note: fm.note ?? "",
        };
        await agent.FamilyMembers.create(fmFormValues as any);
        const { tableData } = get().familyMemberStore;
        const list = new Map(tableData.list);
        if (fm.id) list.set(fm.id, fm);
        set((s: any) => ({ familyMemberStore: { ...s.familyMemberStore, tableData: { ...tableData, list } } }));
    },

    updateFamilyMember: async (fm: FamilyMember) => {
        const { id, firstName, lastName, dateOfBirth, relationShipType, ...rest } = fm;
        const fmFormValues = {
            id,
            firstName,
            lastName,
            dateOfBirth,
            relationShipType: undefined,
            ...rest,
            note: fm.note ?? "",
        };
        await agent.FamilyMembers.update(fmFormValues as any);

        const { tableData } = get().familyMemberStore;
        const list = new Map(tableData.list);
        if (fm.id) {
            const existing = list.get(fm.id);
            list.set(fm.id, { ...(existing || {}), ...fm } as FamilyMember);
        }
        set((s: any) => ({ familyMemberStore: { ...s.familyMemberStore, tableData: { ...tableData, list } } }));
    },

    deleteFamilyMember: async (id: string) => {
        set((s: any) => ({ familyMemberStore: { ...s.familyMemberStore, loading: true } }));
        try {
            await agent.FamilyMembers.delete(id);
            const { tableData } = get().familyMemberStore;
            const list = new Map(tableData.list);
            list.delete(id);
            set((s: any) => ({
                familyMemberStore: {
                    ...s.familyMemberStore,
                    tableData: { ...tableData, list },
                    loading: false,
                },
            }));
        } catch (e) {
            console.error(e);
            set((s: any) => ({ familyMemberStore: { ...s.familyMemberStore, loading: false } }));
        }
    },
});